// WHAT: Tab component for time tracking interface with workspace/aspect selection
// WHY:  Provides main interface for starting, managing, and stopping time tracking
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: ProgressBar component, tracking control hooks
// RISKS:
//   - NOTE: Heartbeat check threshold (3000ms) may be too strict
//   - NOTE: Progress calculation may not handle edge cases
//   - NOTE: Mode switching may not preserve selection state
//   - NOTE: Proximity state may not reflect actual hardware state

import { Activity, Check, Clock, Layers, Plus, Play, Square, Target } from "lucide-react";
import React, { type FC } from "react";

import { Button, Card, ProgressBar } from "@/components/ui";

/**
 * Props for TrackingTab component
 */
interface Properties {
  /** Confirmed workspace */
  confirmedWorkspace: string;
  /** Current options for selection */
  currentOptions: string[];
  /** Current selection index */
  currentSelectionIndex: number;
  /** Daily capacity */
  dailyCapacity: number;
  /** Expected total hours */
  expectedTotal: number;
  /** Stop tracking handler */
  handleStopTracking: () => void;
  /** Whether tracking is active */
  isTracking: boolean;
  /** Last heartbeat timestamp */
  lastHeartbeat: number;
  /** Current selection mode */
  mode: string;
  /** On-track factor */
  onTrackFactor: null | number;
  /** Proximity sensor state */
  proximityState: boolean | null;
  /** Set show manual modal handler */
  setShowManualModal: (show: boolean) => void;
  /** Worked today */
  workedToday: number;
  /** Total worked hours */
  workedTotal: number;
}

/**
 * Renders on-track factor card.
 *
 * @param onTrackFactor - On-track factor value
 *
 * @param workedTotal - Total worked hours
 *
 * @param expectedTotal - Expected total hours
 *
 * @returns React.ReactElement or null
 *
 */
const renderOnTrackCard = (
  onTrackFactor: null | number,
  workedTotal: number,
  expectedTotal: number,
): null | React.ReactElement => {
  if (onTrackFactor === null) {
    return null;
  }
  const factor = onTrackFactor || 0;
  let className = "";
  let label = "";
  if (factor >= 0.85) {
    className = "bg-success-500/20 text-success-400 border border-success-500/30";
    label = "On Track";
  } else if (factor >= 0.7) {
    className = "bg-warning-500/20 text-warning-400 border border-warning-500/30";
    label = "Warnung";
  } else {
    className = "bg-error-500/20 text-error-400 border border-error-500/30";
    label = "Off Track";
  }
  return (
    <Card className="hover:shadow-xl transition-all duration-200" padding="lg" variant="elevated">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-2xl">📊</div>
          <div>
            <div className="text-sm text-neutral-400">On-Track-Faktor</div>
            <div className="text-lg font-bold text-neutral-200">{(factor * 100).toFixed(1)}%</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-neutral-400">Jahresfortschritt</div>
          <div className="text-lg font-bold text-neutral-200">
            {(workedTotal || 0).toFixed(1)}h / {(expectedTotal || 0).toFixed(1)}h
          </div>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${className}`}
        >
          {label}
        </div>
      </div>
    </Card>
  );
};

/**
 * Renders daily progress card.
 *
 * @param workedToday - Hours worked today
 *
 * @param dailyCapacity - Daily capacity
 *
 * @returns React.ReactElement or null
 *
 */
const renderDailyProgressCard = (
  workedToday: number,
  dailyCapacity: number,
): null | React.ReactElement => {
  if (dailyCapacity <= 0) {
    return null;
  }
  const worked = workedToday || 0;
  let state: "error" | "success" | "warning" = "error";
  if (worked >= dailyCapacity) {
    state = "success";
  } else if (worked >= dailyCapacity * 0.8) {
    state = "warning";
  }
  return (
    <Card className="hover:shadow-xl transition-all duration-200" padding="lg" variant="elevated">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Heute gearbeitet</span>
        </div>
        <span className="text-sm text-slate-400">
          {worked.toFixed(1)}h / {dailyCapacity.toFixed(1)}h
        </span>
      </div>
      <ProgressBar
        max={dailyCapacity > 0 ? dailyCapacity : 1}
        showLabel
        size="lg"
        state={state}
        value={worked}
      />
    </Card>
  );
};

/**
 * Renders tracking status card.
 *
 * @param isTracking - Whether tracking is active
 *
 * @param mode - Current mode
 *
 * @returns React.ReactElement
 *
 */
const renderTrackingStatusCard = (isTracking: boolean, mode: string): React.ReactElement => {
  return (
    <Card
      className={`bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 ${
        isTracking ? "border-tracq-bright-blue/30" : "border-slate-600/50"
      }`}
      padding="md"
      variant="default"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isTracking ? "bg-tracq-bright-blue/20" : "bg-slate-600/20"
            }`}
          >
            <Activity
              className={`w-4 h-4 ${isTracking ? "text-tracq-bright-blue" : "text-slate-400"}`}
            />
          </div>
          <div>
            <div className="font-medium text-white">Tracking Status</div>
            <div className="text-sm text-slate-400">
              {isTracking ? "Aktiv erfasst" : "Nicht aktiv"} • Modus: {mode}
            </div>
          </div>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            isTracking
              ? "bg-tracq-bright-blue/20 text-tracq-bright-blue border border-tracq-bright-blue/30"
              : "bg-slate-600/20 text-slate-400 border border-slate-600/30"
          }`}
        >
          {isTracking ? "LIVE" : "IDLE"}
        </div>
      </div>
    </Card>
  );
};

/**
 * Renders proximity status card.
 *
 * @param proximityState - Proximity state
 *
 * @returns React.ReactElement
 *
 */
const renderProximityStatusCard = (proximityState: boolean | null): React.ReactElement => {
  return (
    <Card
      className={`bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 ${
        proximityState ? "border-tracq-bright-blue/30" : "border-slate-600/50"
      }`}
      padding="md"
      variant="default"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              proximityState ? "bg-tracq-bright-blue/20" : "bg-slate-600/20"
            }`}
          >
            <Activity
              className={`w-4 h-4 ${proximityState ? "text-tracq-bright-blue" : "text-slate-400"}`}
            />
          </div>
          <div>
            <div className="font-medium text-white">Proximity Detection</div>
            <div className="text-sm text-slate-400">
              {proximityState ? "Gerät erkannt" : "Kein Gerät in der Nähe"}
            </div>
          </div>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            proximityState
              ? "bg-tracq-bright-blue/20 text-tracq-bright-blue border border-tracq-bright-blue/30"
              : "bg-slate-600/20 text-slate-400 border border-slate-600/30"
          }`}
        >
          {proximityState ? "CONNECTED" : "DISCONNECTED"}
        </div>
      </div>
    </Card>
  );
};

/**
 * Renders heartbeat status card.
 *
 * @param isHeartbeatActive - Whether heartbeat is active
 *
 * @param lastHeartbeat - Last heartbeat timestamp
 *
 * @returns React.ReactElement
 *
 */
const renderHeartbeatStatusCard = (
  isHeartbeatActive: boolean,
  lastHeartbeat: number,
): React.ReactElement => {
  return (
    <Card
      className={`bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200 ${
        isHeartbeatActive ? "border-tracq-bright-blue/30" : "border-slate-600/50"
      }`}
      padding="md"
      variant="default"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isHeartbeatActive ? "bg-tracq-bright-blue/20" : "bg-slate-600/20"
            }`}
          >
            <Clock
              className={`w-4 h-4 ${isHeartbeatActive ? "text-tracq-bright-blue" : "text-slate-400"}`}
            />
          </div>
          <div>
            <div className="font-medium text-white">Buzzer Heartbeat</div>
            <div className="text-sm text-slate-400">
              {isHeartbeatActive ? "Verbindung aktiv" : "Verbindung unterbrochen"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              isHeartbeatActive ? "bg-tracq-bright-blue animate-pulse" : "bg-slate-500"
            }`}
          />
          <span className="text-xs text-slate-500 font-mono">
            {lastHeartbeat > 0
              ? new Date(lastHeartbeat).toLocaleTimeString()
              : "Waiting for signal..."}
          </span>
        </div>
      </div>
    </Card>
  );
};

/**
 * Renders system status cards.
 *
 * @param isTracking - Whether tracking is active
 *
 * @param mode - Current mode
 *
 * @param proximityState - Proximity state
 *
 * @param isHeartbeatActive - Whether heartbeat is active
 *
 * @param lastHeartbeat - Last heartbeat timestamp
 *
 * @returns React.ReactElement
 *
 */
const renderSystemStatus = (
  isTracking: boolean,
  mode: string,
  proximityState: boolean | null,
  isHeartbeatActive: boolean,
  lastHeartbeat: number,
): React.ReactElement => {
  return (
    <div className="flex-1">
      <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-tracq-bright-blue" />
        System Status
      </h4>

      <div className="space-y-3">
        {renderTrackingStatusCard(isTracking, mode)}
        {renderProximityStatusCard(proximityState)}
        {renderHeartbeatStatusCard(isHeartbeatActive, lastHeartbeat)}
      </div>
    </div>
  );
};

/**
 * WHAT:
 *   Tab component that provides main time tracking interface with workspace/aspect
 *   selection, tracking controls, and progress indicators.
 *
 * WHY:
 *   Enables users to start and manage time tracking sessions with visual feedback
 *   and progress tracking.
 *
 * PROPS:
 *   - mode: string — Current selection mode
 *   - currentOptions: string[] — Available options
 *   - currentSelectionIndex: number — Selected index
 *   - confirmedWorkspace: string — Confirmed workspace
 *   - isTracking: boolean — Tracking state
 *   - handleStopTracking: () => void — Stop handler
 *   - setShowManualModal: (show: boolean) => void — Manual modal handler
 *   - proximityState: boolean | null — Proximity state
 *   - lastHeartbeat: number — Last heartbeat
 *   - onTrackFactor: number | null — On-track factor
 *   - workedTotal: number — Total worked
 *   - expectedTotal: number — Expected total
 *   - workedToday: number — Worked today
 *   - dailyCapacity: number — Daily capacity
 *
 * BEHAVIOR:
 *   - Displays workspace/aspect selection interface
 *   - Shows tracking status and controls
 *   - Displays progress indicators
 *   - Shows proximity state
 *   - Provides manual entry option
 *
 * RISKS:
 *   - NOTE: Heartbeat check threshold (3000ms) may be too strict
 *   - NOTE: Progress calculation may not handle edge cases
 *   - NOTE: Mode switching may not preserve selection state
 *   - NOTE: Proximity state may not reflect actual hardware state
 *
 * @param root0
 *
 * @param root0.mode
 *
 * @param root0.currentOptions
 *
 * @param root0.currentSelectionIndex
 *
 * @param root0.confirmedWorkspace
 *
 * @param root0.isTracking
 *
 * @param root0.handleStopTracking
 *
 * @param root0.setShowManualModal
 *
 * @param root0.proximityState
 *
 * @param root0.lastHeartbeat
 *
 * @param root0.onTrackFactor
 *
 * @param root0.workedTotal
 *
 * @param root0.expectedTotal
 *
 * @param root0.workedToday
 *
 * @param root0.dailyCapacity
 *
 * @returns JSX.Element
 *
 */
export const TrackingTab: FC<Properties> = ({
  confirmedWorkspace,
  currentOptions,
  currentSelectionIndex,
  dailyCapacity,
  expectedTotal,
  handleStopTracking,
  isTracking,
  lastHeartbeat,
  mode,
  onTrackFactor,
  proximityState,
  setShowManualModal,
  workedToday,
  workedTotal,
}) => {
  // NOTE: Check if heartbeat exists and is recent (within 3 seconds)
  const isHeartbeatActive = lastHeartbeat > 0 && Date.now() - lastHeartbeat < 3000;

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
              isTracking
                ? "bg-success-500/20 border border-success-500/30"
                : "bg-neutral-500/20 border border-neutral-500/30"
            }`}
          >
            {isTracking ? (
              <Play className="w-5 h-5 text-success-400" />
            ) : (
              <Square className="w-5 h-5 text-neutral-400" />
            )}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-neutral-200">Tracking</h3>
            <p className="text-neutral-400 text-sm">
              {isTracking ? "Aktive Zeiterfassung" : "Bereit für Zeiterfassung"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm text-neutral-400">Status</div>
            <div
              className={`text-xl font-bold ${isTracking ? "text-success-400" : "text-neutral-400"}`}
            >
              {isTracking ? "Live" : "Idle"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-neutral-400">Modus</div>
            <div className="text-xl font-bold text-neutral-200 capitalize">{mode}</div>
          </div>
        </div>
      </div>

      {/* On-Track-Faktor Anzeige */}
      {renderOnTrackCard(onTrackFactor, workedTotal, expectedTotal)}

      {/* Täglicher Fortschritt */}
      {renderDailyProgressCard(workedToday, dailyCapacity)}

      {/* Current Selection */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200"
        padding="lg"
        variant="default"
      >
        <div className="flex items-center gap-3 mb-4">
          {mode === "workspace" ? (
            <Layers className="w-5 h-5 text-tracq-bright-blue" />
          ) : (
            <Target className="w-5 h-5 text-tracq-deep-orange" />
          )}
          <h4 className="text-lg font-semibold text-white">
            {mode === "workspace"
              ? "Choose your Workspace "
              : `Choose your Aspect (${confirmedWorkspace})`}
          </h4>
        </div>

        <div className="space-y-2">
          {currentOptions.map((opt, index) => (
            <div
              className={`group p-3 rounded-lg transition-all duration-200 flex items-center justify-between cursor-pointer ${
                index === currentSelectionIndex
                  ? "bg-tracq-bright-blue/20 border border-tracq-bright-blue/30 text-tracq-bright-blue"
                  : "bg-slate-600/50 border border-slate-500 text-slate-200 hover:bg-slate-600 hover:border-slate-400"
              }`}
              key={opt}
            >
              <span className={index === currentSelectionIndex ? "font-semibold" : ""}>{opt}</span>
              {index === currentSelectionIndex && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-tracq-bright-blue rounded-full animate-pulse" />
                  <Check className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          className="flex-1 flex items-center justify-center gap-2"
          disabled={!isTracking}
          onClick={handleStopTracking}
          variant="danger"
        >
          <Square className="w-4 h-4" />
          Stop Tracking
        </Button>
        <Button
          className="flex-1 flex items-center justify-center gap-2"
          onClick={() => {
            setShowManualModal(true);
          }}
          variant="primary"
        >
          <Plus className="w-4 h-4" />
          Manuell erfassen
        </Button>
      </div>

      {/* System Status */}
      {renderSystemStatus(isTracking, mode, proximityState, isHeartbeatActive, lastHeartbeat)}

      {/* Footer Info */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200"
        padding="md"
        variant="default"
      >
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-tracq-bright-blue rounded-full" />
            <span className="text-slate-300">
              {mode === "workspace"
                ? "Workspace-Modus aktiv"
                : `Aspect-Modus für ${confirmedWorkspace}`}
            </span>
          </div>
          <div className="text-slate-500">{currentOptions.length} Optionen verfügbar</div>
        </div>
      </Card>
    </div>
  );
};
